import { clsx } from 'clsx';
import * as React from 'react';

import type { TextareaProps } from '../textarea';
import { Textarea } from '../textarea';
import styles from './text-field.module.scss';

export type TextFieldProps = Omit<TextareaProps, 'classes'> & {
  label?: string;
  helperText?: string;
  error?: string;

  classes?: {
    textarea?: TextareaProps['className'];
    root?: string;
    head?: string;
    footer?: string;
  };
};

export const TextFieldRoot = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div ref={ref} className={clsx(styles.root, className)}>
    {children}
  </div>
));
TextFieldRoot.displayName = 'TextFieldRoot';

export const TextFieldHead = React.forwardRef<
  HTMLLabelElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <label ref={ref} className={clsx(styles.head, className)}>
    {children}
  </label>
));
TextFieldHead.displayName = 'TextFieldHead';

export const TextFieldControl = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div ref={ref} className={clsx(styles.control, className)}>
    {children}
  </div>
));
TextFieldControl.displayName = 'TextFieldControl';

export const TextFieldFooter = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div ref={ref} className={clsx(styles.footer, className)}>
    {children}
  </div>
));
TextFieldFooter.displayName = 'TextFieldFooter';

export const TextAreaField = React.forwardRef<
  HTMLTextAreaElement,
  TextFieldProps
>(
  (
    { label, helperText, error, className, classes, hasError, ...props },
    ref
  ) => {
    const showHelperText = hasError ? error : helperText;

    return (
      <TextFieldRoot className={clsx(classes?.root, className)}>
        {label && (
          <TextFieldHead className={classes?.head}>{label}</TextFieldHead>
        )}

        <Textarea
          ref={ref}
          hasError={hasError}
          className={clsx(classes?.textarea)}
          {...props}
        />

        {showHelperText && (
          <TextFieldFooter className={classes?.footer}>
            <span
              className={clsx(styles.helperText, {
                [styles.errorText]: hasError,
              })}
            >
              {error ?? helperText}
            </span>
          </TextFieldFooter>
        )}
      </TextFieldRoot>
    );
  }
);
